/* One native responsive image, one optional 96px preview. Never replace the
   image node or its src to display a preview: links, variants, zoom and browser
   candidate selection continue to address the real image. */
(() => {
  if (window.ThemeProductImages) return;
  const selector = 'img[data-product-image]';
  const states = new Map();
  const layers = new WeakSet();
  const frames = new Map();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const FADE = 220;
  const signature = image => [image.getAttribute('src'), image.getAttribute('srcset'), image.getAttribute('sizes'), image.dataset.productImage, image.dataset.productPreview].join('|');

  // Also works for detached images and externally choreographed surfaces.
  function ready(image, { signal, timeout = 0 } = {}) {
    if (!image || signal?.aborted) return Promise.resolve(false);
    return new Promise(resolve => {
      let finished = false;
      let timer;
      const finish = value => {
        if (finished) return;
        finished = true;
        clearTimeout(timer);
        image.removeEventListener('load', loaded);
        image.removeEventListener('error', failed);
        signal?.removeEventListener('abort', failed);
        resolve(value);
      };
      const failed = () => finish(false);
      const loaded = async () => {
        const source = image.currentSrc || image.src;
        try { await image.decode?.(); } catch (_) { /* Native rendering is valid when decode rejects. */ }
        if (finished || signal?.aborted) return finish(false);
        if (source !== (image.currentSrc || image.src)) {
          if (image.complete && image.naturalWidth) loaded();
          return;
        }
        finish(image.complete && image.naturalWidth > 0);
      };
      image.addEventListener('load', loaded);
      image.addEventListener('error', failed);
      signal?.addEventListener('abort', failed, { once: true });
      if (timeout > 0) timer = setTimeout(failed, timeout);
      if (image.complete && (image.currentSrc || image.getAttribute('src'))) image.naturalWidth ? loaded() : failed();
    });
  }

  function clearPreview(state) {
    state.resize?.disconnect();
    state.layer?.remove();
    state.layer = null;
    state.image?.removeAttribute('data-image-preview-visible');
    if (state.frame) {
      const frame = frames.get(state.frame);
      if (frame && --frame.users === 0) {
        if (frame.managed && state.frame.style.position === 'relative') state.frame.style.position = frame.position;
        frames.delete(state.frame);
      }
      state.frame = null;
    }
    if (state.positionedImage) {
      state.positionedImage.style.position = state.originalPosition;
      state.positionedImage = null;
    }
    clearTimeout(state.cleanup);
  }
  function release(image) {
    const state = states.get(image);
    if (!state) return;
    state.controller.abort();
    state.animation?.cancel();
    clearTimeout(state.deadline);
    clearPreview(state);
    observer?.unobserve(image);
    image.removeAttribute('data-image-pending');
    image.removeAttribute('data-image-failed');
    states.delete(image);
  }

  async function preview(image, state) {
    if (!image.dataset.productPreview || image.dataset.productImage !== 'auto') return;
    const small = new Image();
    small.decoding = 'async';
    small.fetchPriority = 'low';
    small.src = image.dataset.productPreview;
    if (!await ready(small, { signal: state.controller.signal }) || states.get(image) !== state || state.ok || !image.isConnected) return;
    const parent = image.parentElement;
    const style = getComputedStyle(image);
    // Hidden alternates never cast a preview over the working front image.
    if (style.display === 'none' || image.classList.contains('pl-alt') || image.classList.contains('alt') || image.classList.contains('image-2')) return;
    let frame = frames.get(parent);
    if (!frame) {
      frame = { users: 0, position: parent.style.position, managed: getComputedStyle(parent).position === 'static' };
      frames.set(parent, frame);
      if (frame.managed) parent.style.position = 'relative';
    }
    frame.users++;
    state.frame = parent;
    if (style.position === 'static') {
      state.originalPosition = image.style.position;
      state.positionedImage = image;
      image.style.position = 'relative';
    }
    const layer = document.createElement('span');
    layer.setAttribute('data-product-preview-layer', '');
    layer.setAttribute('aria-hidden', 'true');
    Object.assign(layer.style, {
      backgroundImage: `url(${JSON.stringify(small.src)})`,
      backgroundSize: style.objectFit === 'cover' ? 'cover' : style.objectFit === 'fill' ? '100% 100%' : 'contain',
      backgroundPosition: style.objectPosition,
      filter: `blur(5px) ${style.filter === 'none' ? '' : style.filter}`,
      transform: style.transform, transformOrigin: style.transformOrigin,
      borderRadius: style.borderRadius, zIndex: style.zIndex
    });
    const size = () => Object.assign(layer.style, {
      left: `${image.offsetLeft}px`, top: `${image.offsetTop}px`,
      width: `${image.offsetWidth}px`, height: `${image.offsetHeight}px`
    });
    state.layer = layer;
    layers.add(layer);
    image.setAttribute('data-image-preview-visible', '');
    image.before(layer);
    size();
    if ('ResizeObserver' in window) { state.resize = new ResizeObserver(size); state.resize.observe(image); }
  }

  function activate(image) {
    const state = states.get(image);
    if (!state || state.active || state.done) return;
    state.active = true;
    observer?.unobserve(image);
    if (image.dataset.productImage === 'external') return;
    preview(image, state);
    // Start only when near the viewport. A slow network/decode cannot leave a
    // good native bitmap permanently hidden, or time out an offscreen image.
    state.deadline = setTimeout(() => {
      if (states.get(image) !== state) return;
      state.timedOut = true;
      image.removeAttribute('data-image-pending');
    }, 8000);
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) activate(entry.target); });
  }, { rootMargin: '300px' }) : null;

  function watch(image) {
    if (!image.isConnected || !image.getAttribute('src')) return;
    const key = signature(image);
    if (states.get(image)?.key === key && states.get(image)?.parent === image.parentElement) return;
    release(image);
    image.removeAttribute('data-image-pending');
    image.removeAttribute('data-image-failed');
    image.removeAttribute('data-image-preview-visible');
    const cached = image.complete && image.naturalWidth > 0;
    const external = image.dataset.productImage === 'external';
    const state = { key, image, parent: image.parentElement, controller: new AbortController(), cached, done: false };
    states.set(image, state);
    image.addEventListener('load', () => {
      if (state.done && !state.ok && states.get(image) === state) { release(image); watch(image); }
    }, { signal: state.controller.signal });
    image.removeAttribute('data-image-ready');
    if (!cached && !external) image.setAttribute('data-image-pending', '');
    ready(image, { signal: state.controller.signal }).then(ok => {
      if (states.get(image) !== state || !image.isConnected || state.controller.signal.aborted) return;
      state.done = true;
      state.ok = ok;
      clearTimeout(state.deadline);
      observer?.unobserve(image);
      image.removeAttribute('data-image-pending');
      if (!ok) {
        if (!external) image.setAttribute('data-image-failed', '');
        image.dispatchEvent(new CustomEvent('product-image:error', { bubbles: true }));
        return;
      }
      image.setAttribute('data-image-ready', '');
      const opacity = getComputedStyle(image).opacity;
      if (!cached && !external && !motion.matches && !state.timedOut && Number(opacity) > 0 && image.animate) {
        state.animation = image.animate([{ opacity: 0 }, { opacity }], { duration: FADE, easing: 'cubic-bezier(.22,1,.36,1)' });
        // Keep a transparent PNG's preview underneath until the sharp layer is
        // opaque; fading both layers in opposite directions produces a dip.
        state.cleanup = setTimeout(() => clearPreview(state), FADE);
      } else clearPreview(state);
      image.dispatchEvent(new CustomEvent('product-image:ready', { bubbles: true }));
    });
    if (!cached && !external) {
      if (image.loading !== 'lazy' || !observer) activate(image);
      else observer.observe(image);
    }
  }
  function init(root = document) {
    root.querySelectorAll?.('[data-product-preview-layer]').forEach(layer => { if (!layers.has(layer)) layer.remove(); });
    if (root.matches?.(selector)) watch(root);
    root.querySelectorAll?.(selector).forEach(watch);
  }
  // JSON-fed carts and older collection/search responses use the same width
  // ladder and preview size as product-image-attributes.liquid.
  function create(source, { sizes = '(max-width: 767px) 50vw, 25vw', cap = 1600, loading = 'lazy', alt = '', reveal = 'auto' } = {}) {
    const image = document.createElement('img');
    const url = new URL(source.src || source.url || source, document.baseURI);
    const width = size => { url.searchParams.set('width', size); return url.href; };
    const limit = Math.min(cap, Number(source.width) || cap);
    image.dataset.productImage = reveal;
    image.dataset.productPreview = width(96);
    image.loading = loading;
    image.decoding = 'async';
    image.sizes = `${loading === 'lazy' ? 'auto, ' : ''}${sizes}`;
    image.width = Number(source.width) || limit;
    image.height = Number(source.height) || limit;
    image.alt = alt;
    image.srcset = [...[48,96,160,240,360,480,640,800,1000,1200,1600,1800,2400].filter(size => size < limit), limit].map(size => `${width(size)} ${size}w`).join(', ');
    image.src = width(limit);
    return image;
  }
  function load(image, sources = {}) {
    if (!image) return;
    if (sources.sizes) image.sizes = sources.sizes;
    if (sources.preview) image.dataset.productPreview = sources.preview;
    if (sources.srcset !== undefined) image.srcset = sources.srcset;
    if (sources.src) image.src = sources.src;
    image.loading = 'eager';
    if (!image.hasAttribute('data-product-image')) image.dataset.productImage = 'auto';
    watch(image);
    activate(image);
  }
  window.ThemeProductImages = { init, load, ready, create };
  init();
  new MutationObserver(records => {
    let removed = false;
    for (const record of records) {
      if (record.type === 'attributes') {
        if (record.target.matches(selector)) watch(record.target);
      } else {
        record.addedNodes.forEach(node => { if (node.nodeType === 1) init(node); });
        removed ||= record.removedNodes.length > 0;
      }
    }
    if (removed) for (const image of states.keys()) if (!image.isConnected) release(image);
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'sizes', 'data-product-image', 'data-product-preview'] });
  document.addEventListener('shopify:section:load', event => init(event.target));
  motion.addEventListener('change', () => {
    if (motion.matches) for (const state of states.values()) { state.animation?.cancel(); if (state.done) clearPreview(state); }
  });
})();
